import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const saintLaurentDuVar: ChauffeurPage = {
  slug: "saint-laurent-du-var",
  title: "Chauffeur privé à Saint-Laurent-du-Var – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Saint-Laurent-du-Var : navette aéroport de Nice express, CAP3000, port et plages, 7j/7.",
  keywords: [
    "chauffeur privé saint-laurent-du-var",
    "vtc saint-laurent-du-var",
    "chauffeur saint-laurent-du-var",
    "navette aéroport nice saint-laurent",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Saint-Laurent-du-Var",
    subtitle:
      "À deux pas de l’aéroport de Nice : navette express (~8 min), CAP3000, port de plaisance et bord du Var — 24h/24, 7j/7.",
    keywords: [
      "VTC Saint-Laurent-du-Var",
      "navette aéroport Nice",
      "chauffeur CAP3000",
      "chauffeur port Saint-Laurent",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Saint-Laurent-du-Var, près de l’aéroport de Nice",
    badges: [
      "Navette aéroport express",
      "Suivi de vol",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Saint-Laurent-du-Var",
    items: [
      {
        title: "Aéroport de Nice à ~8 min",
        description:
          "Commune voisine de l’aéroport, séparée par le seul Var : navette express dans les deux sens, suivi de vol et accueil aux terminaux.",
      },
      {
        title: "CAP3000 & port de plaisance",
        description:
          "L’un des plus grands centres commerciaux de France et le port bordé de restaurants : dépose au plus près.",
      },
      {
        title: "Bord du Var & plages",
        description:
          "Berges du Var, promenade et plages : déplacements de bord de mer sans souci de stationnement.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Saint-Laurent-du-Var",
    services: [
      {
        title: "Navette aéroport de Nice express",
        text: "Saint-Laurent-du-Var ⇄ aéroport de Nice Côte d’Azur en ~8 min, idéale pour les vols matinaux et les correspondances serrées, avec suivi de vol.",
      },
      {
        title: "CAP3000",
        text: "Dépose et reprise au pied du centre commercial, y compris chargé de bagages ou d’achats.",
      },
      {
        title: "Port de Saint-Laurent-du-Var",
        text: "Liaison vers le port de plaisance et ses restaurants, retours de soirée inclus.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour vos déplacements sur la Côte d’Azur.",
      },
      {
        title: "Longue distance & liaisons Riviera",
        text: "Départs depuis Saint-Laurent vers Cannes, Monaco, l’Italie ou les stations de ski, au départ tout proche de l’aéroport.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      {
        name: "CAP3000",
        description:
          "Grand centre commercial, en bord de mer près de l’aéroport.",
      },
      {
        name: "Port de Saint-Laurent-du-Var",
        description: "Port de plaisance et restaurants du front de mer.",
      },
      {
        name: "Berges du Var",
        description: "Promenade le long du fleuve, face à Nice.",
      },
      {
        name: "Plages & promenade",
        description: "Front de mer et plages de la commune.",
      },
      {
        name: "Gare de Saint-Laurent-du-Var",
        description: "Liaison ferroviaire et pôle d’échanges.",
      },
      {
        name: "Aéroport de Nice",
        description: "~8 min, de l’autre côté du Var.",
      },
    ],
  },
  generic: {
    heading: "Saint-Laurent-du-Var, à un pont de l’aéroport de Nice",
    subheading:
      "Séparée de l’aéroport par le seul Var, la ville idéale pour une navette express et le shopping à CAP3000.",
    paragraphs: [
      "Saint-Laurent-du-Var jouxte l’aéroport de Nice : seul le fleuve Var sépare la commune des pistes. C’est la position rêvée pour une navette express — comptez environ 8 minutes jusqu’aux terminaux — précieuse pour un vol matinal, un départ groupé ou une correspondance serrée.",
      "Le long du littoral, CAP3000, l’un des plus grands centres commerciaux de France, côtoie le port de plaisance et ses restaurants. Le chauffeur vous y dépose au plus près et vous reprend chargé de bagages ou d’achats, sans que vous cherchiez une place.",
      "Entre les berges du Var, les plages et la promenade, Saint-Laurent est aussi un point de départ commode pour toute la Côte d’Azur : Cannes, Monaco, l’Italie ou les stations de ski, au départ tout proche de l’aéroport, avec suivi de vol et accueil personnalisé.",
    ],
    bullets: [
      "Navette aéroport ~8 min",
      "CAP3000 & port de plaisance",
      "Départ Riviera tout proche de l’aéroport",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Saint-Laurent-du-Var",
    steps: [
      "Indiquez votre vol ou vos adresses (aéroport, CAP3000, port, plages).",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure, même très tôt.",
      "Navette express, dépose au port ou à CAP3000, reprise sur demande.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Saint-Laurent-du-Var"),
    {
      q: "Combien de temps pour l’aéroport de Nice depuis Saint-Laurent-du-Var ?",
      a: "Environ 8 minutes seulement : la commune est voisine de l’aéroport, de l’autre côté du Var. C’est idéal pour un vol matinal ou une correspondance serrée, et nous suivons votre vol dans les deux sens.",
    },
    {
      q: "Puis-je réserver une navette de dernière minute pour l’aéroport ?",
      a: "Oui, la proximité immédiate de l’aéroport facilite les demandes de dernière minute selon disponibilité ; réservez toutefois à l’avance pour les vols très matinaux et en haute saison.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Saint-Laurent-du-Var ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et le volume de bagages.",
    },
    {
      q: "Desservez-vous CAP3000 ?",
      a: "Oui, nous déposons et reprenons au pied du centre commercial CAP3000, y compris chargé de bagages ou d’achats, sans que vous ayez à chercher une place.",
    },
    {
      q: "Assurez-vous les trajets vers le port et ses restaurants ?",
      a: "Oui, nous relions le port de plaisance de Saint-Laurent-du-Var et ses restaurants, avec retours de soirée en toute sérénité.",
    },
  ],
  relatedLinks: [
    {
      label: "Transferts depuis l’aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    { label: "Voiture avec chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Voiture avec chauffeur sur la Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    { label: "Transferts avec chauffeur à Cannes", href: "/chauffeur/cannes" },
  ],
  cta: {
    title: "Votre chauffeur privé à Saint-Laurent-du-Var",
    text: "Navette aéroport express, CAP3000 ou port — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Saint-Laurent-du-Var — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Saint-Laurent-du-Var : navette aéroport de Nice express (~8 min), CAP3000, port et plages. Bilingues, 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/saint-laurent-du-var",
  },
};

export default saintLaurentDuVar;
